// 📍 근태 등록 창 (2026-11-05 형 "더보기에서 각 코드별로 근태등록을 누를 때 GPS") — App 전역 호스트, ATTENDANCE_OPEN_EVENT 로 연다.
//   흐름: (첫 사용) 위치정보 수집 동의 → 근태 종류 선택 → 고정밀 위치 1회 측정 → 자동 장소(사업장 이름/주소) 확인·수정 + 메모 → 등록
//   장소는 사용자가 고칠 수 있지만 좌표·정확도는 항상 따로 저장된다(형 "사용자가 수정 또는 입력, 단 위치기록은 필요").
//   🔴 입력이 있는 창이라 바깥 클릭으로 닫지 않는다(X·ESC·뒤로가기).
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DetailModal from '../common/DetailModal';
import { Icon } from '../icons';
import Spinner from '../Spinner';
import { tptAlert, humanizeError } from '../../stores/modalStore';
import {
	ATTENDANCE_OPEN_EVENT, attCodeName, fetchMyAttendance, getBestFix, giveAttConsent, resolveAttPlace, submitAttendance, syncAttConfig,
	type AttCode, type AttConfig, type AttFix, type AttRecord, type AttResolved,
} from '../../api/attendance';

type Step = { k: 'pick' } | { k: 'locating'; code: AttCode; acc: number | null } | { k: 'confirm'; code: AttCode; fix: AttFix; auto: AttResolved };

const ERR_KEYS: Record<string, string> = { unsupported: 'att.err_unsupported', denied: 'att.err_denied', unavailable: 'att.err_timeout', timeout: 'att.err_timeout', consent: 'att.err_consent', disabled: 'att.err_disabled' };

export default function AttendanceHost() {
	const { t, i18n } = useTranslation();
	const lang = (i18n.language || 'ko').slice(0, 2);
	const [open, setOpen] = useState(false);
	const [cfg, setCfg] = useState<AttConfig | null>(null);
	const [step, setStep] = useState<Step>({ k: 'pick' });
	const [place, setPlace] = useState('');
	const [note, setNote] = useState('');
	const [busy, setBusy] = useState(false);
	const [recent, setRecent] = useState<AttRecord[] | null>(null);

	useEffect(() => {
		const onOpen = () => { setOpen(true); setStep({ k: 'pick' }); void load(); };
		window.addEventListener(ATTENDANCE_OPEN_EVENT, onOpen);
		return () => window.removeEventListener(ATTENDANCE_OPEN_EVENT, onOpen);
	}, []);
	const load = async () => {
		setCfg(await syncAttConfig(true));
		fetchMyAttendance().then(setRecent).catch(() => setRecent([]));
	};
	const errText = (e: unknown) => { const k = ERR_KEYS[(e as Error)?.message || '']; return k ? t(k) : humanizeError(e); };

	const measure = async (code: AttCode) => {
		setStep({ k: 'locating', code, acc: null });
		try {
			const fix = await getBestFix((acc) => setStep((s) => (s.k === 'locating' ? { ...s, acc } : s)));
			const auto = await resolveAttPlace(fix, lang);
			setPlace(auto.place); setNote('');
			setStep({ k: 'confirm', code, fix, auto });
		} catch (e) {
			setStep({ k: 'pick' });
			void tptAlert(errText(e));
		}
	};
	const submit = async () => {
		if (step.k !== 'confirm') return;
		setBusy(true);
		try {
			const rec = await submitAttendance({ ...step.fix, code: step.code.code, place, note, lang });
			setRecent((cur) => [rec, ...(cur || [])].slice(0, 50));
			setStep({ k: 'pick' });
			void tptAlert(t('att.done', { code: attCodeName(step.code, lang), place: rec.place || '-' }));
		} catch (e) { void tptAlert(errText(e)); }
		finally { setBusy(false); }
	};
	const agree = async () => {
		setBusy(true);
		try { await giveAttConsent(); setCfg(await syncAttConfig(true)); }
		catch (e) { void tptAlert(errText(e)); }
		finally { setBusy(false); }
	};

	const codeOf = (r: AttRecord) => cfg?.codes.find((c) => c.code === r.code);
	const fmtAt = (iso: string) => { try { return new Date(iso).toLocaleString(i18n.language, { month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };
	// 고객사·협력사 반경 안 = '고객사 도착 — 이름' (2026-11-05 형 "고객사나 협력사로도 출장")
	const siteLine = (a: Pick<AttResolved, 'inSite' | 'siteName' | 'siteKind' | 'distM' | 'hasSites'>) => !a.siteName
		? t('att.noSite')
		: a.inSite
			? (a.siteKind && a.siteKind !== 'own' ? t('att.arrived', { kind: t(`att.kind_${a.siteKind}`), name: a.siteName }) : t('att.inSite', { name: a.siteName }))
			: t('att.outSite', { name: a.siteName, m: a.distM ?? '-' });

	return (
		<DetailModal open={open} onClose={() => setOpen(false)} title={t('att.title')} width={440} closeOnBackdrop={false} closeButton testId="att-modal" bodyClassName="flex flex-col gap-4 p-4">
			{!cfg ? (
				<div className="flex justify-center py-8"><Spinner /></div>
			) : !cfg.consent ? (
				<div className="flex flex-col gap-3" data-testid="att-consent">
					<b className="flex items-center gap-2 text-ui-base text-fg"><Icon name="shield" size={18} className="text-class-blue" />{t('att.consentTitle')}</b>
					<p className="whitespace-pre-wrap text-ui-sm leading-relaxed text-fg-muted">{t('att.consentBody')}</p>
					<button type="button" disabled={busy} onClick={() => void agree()} data-testid="att-agree"
						className="rounded-lg bg-class-blue px-4 py-2.5 text-ui-sm font-bold text-white disabled:opacity-40">{t('att.agree')}</button>
				</div>
			) : step.k === 'pick' ? (
				<>
					<div className="flex flex-col gap-2">
						<span className="text-ui-xs font-semibold text-fg-subtle">{t('att.pickCode')}</span>
						<div className="grid grid-cols-2 gap-2">
							{cfg.codes.map((c) => (
								<button key={c.code} type="button" onClick={() => void measure(c)} data-testid={`att-code-${c.code}`}
									className="flex items-center justify-center gap-1.5 rounded-xl border border-line bg-surface-subtle px-3 py-3.5 text-ui-base font-bold text-fg hover:bg-hover active:bg-hover-strong">
									<Icon name="clock" size={16} className="shrink-0 text-class-blue" />{attCodeName(c, lang)}
								</button>
							))}
						</div>
					</div>
					<div className="flex flex-col gap-1.5">
						<span className="text-ui-xs font-semibold text-fg-subtle">{t('att.recent')}</span>
						{recent === null ? <Spinner /> : recent.length === 0 ? <span className="text-ui-xs text-fg-faint">{t('att.empty')}</span> : (
							<ul className="flex flex-col divide-y divide-line-subtle rounded-lg border border-line" data-testid="att-recent">
								{recent.slice(0, 20).map((r) => (
									<li key={r._id} className="flex items-center gap-2 px-2.5 py-2 text-ui-xs">
										<b className="w-16 shrink-0 truncate text-fg">{attCodeName(codeOf(r), lang) || r.codeName}</b>
										<span className="min-w-0 flex-1 truncate text-fg-muted" title={r.place}>{r.place || '-'}</span>
										{r.siteName && <span className={`shrink-0 rounded px-1 text-[10px] font-bold ${r.inSite ? 'bg-class-blue/10 text-class-blue' : 'bg-surface-strong text-fg-subtle'}`}>{r.inSite ? t('att.in') : t('att.out')}</span>}
										<time className="shrink-0 tabular-nums text-fg-faint">{fmtAt(r.at)}</time>
									</li>
								))}
							</ul>
						)}
					</div>
				</>
			) : step.k === 'locating' ? (
				<div className="flex flex-col items-center gap-3 py-8 text-ui-sm text-fg-muted" data-testid="att-locating">
					<Spinner />
					<b className="text-fg">{attCodeName(step.code, lang)}</b>
					<span>{step.acc ? t('att.locating', { m: Math.round(step.acc) }) : t('att.locatingStart')}</span>
				</div>
			) : (
				<div className="flex flex-col gap-3" data-testid="att-confirm">
					<div className="flex items-center gap-2">
						<b className="rounded-lg bg-class-blue px-2.5 py-1 text-ui-sm text-white">{attCodeName(step.code, lang)}</b>
						<span className="ml-auto text-ui-xs text-fg-subtle">{t('att.accuracy', { m: Math.round(step.fix.acc) })}</span>
					</div>
					<span className={`flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-ui-xs font-semibold ${step.auto.inSite ? 'bg-class-blue/10 text-class-blue' : 'bg-surface-muted text-fg-muted'}`} data-testid="att-site">
						<Icon name="pin" size={14} className="shrink-0" />{siteLine(step.auto)}
					</span>
					<label className="flex flex-col gap-1 text-ui-xs font-semibold text-fg-subtle">{t('att.place')}
						<input value={place} onChange={(e) => setPlace(e.target.value.slice(0, 120))} maxLength={120} data-testid="att-place"
							className="rounded-lg border border-line bg-surface px-2.5 py-2 text-ui-sm font-normal text-fg outline-none focus:border-class-blue" />
						<span className="font-normal text-fg-faint">{t('att.placeHint')}</span>
					</label>
					<label className="flex flex-col gap-1 text-ui-xs font-semibold text-fg-subtle">{t('att.note')}
						<textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={2} maxLength={500} data-testid="att-note"
							placeholder={step.auto.siteName && !step.auto.inSite ? t('att.notePh') : ''}
							className="resize-none rounded-lg border border-line bg-surface px-2.5 py-2 text-ui-sm font-normal text-fg outline-none focus:border-class-blue" />
					</label>
					<div className="flex items-center gap-2">
						<button type="button" disabled={busy} onClick={() => setStep({ k: 'pick' })} className="rounded-lg border border-line px-3 py-2 text-ui-sm text-fg-muted hover:bg-hover">{t('att.back')}</button>
						<button type="button" disabled={busy} onClick={() => void measure(step.code)} className="rounded-lg border border-line px-3 py-2 text-ui-sm text-fg-muted hover:bg-hover">{t('att.remeasure')}</button>
						<button type="button" disabled={busy} onClick={() => void submit()} data-testid="att-submit"
							className="ml-auto rounded-lg bg-class-blue px-5 py-2 text-ui-sm font-bold text-white disabled:opacity-40">{t('att.submit')}</button>
					</div>
				</div>
			)}
		</DetailModal>
	);
}
